require('./RAProjectionChart.scss');

// @vendor
const PropTypes = require('prop-types');
const React = require('react');
const {
    ComposedChart,
    ResponsiveContainer,
    Line,
    Area,
    YAxis,
    XAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ReferenceDot,
} = require('recharts');
const classnames = require('classnames');
const { isEmpty, get } = require('lodash');

// @components
const RAProjectionChartTooltip = require('./RAProjectionChartTooltip');
const RAProjectionChartCustomTick = require('./RAProjectionChartCustomTick');
const RACustomTickWithObjective = require('./RACustomTickWithObjective/RACustomTickWithObjective');
const RAOneLineSymbolAmount = require('../commons/RAOneLineSymbolAmount/RAOneLineSymbolAmount');
const RACustomizedCursorLine = require('../RACustomizedCursorLine/RACustomizedCursorLine');

//@utilities
const CurrencyHelper = require('utilities/currencyHelper');
const { FormattedText } = require('core/i18n').i18n;
const {
    getShowPonitsAndDayTerm,
} = require('../../utilities/RAObjectiveTrackingGetYearAndNoOfPoints');

// @constants
const { EURO_SYMBOL, EURO_TEXT } = require('constants/index');
const { DATA } = require('./constants');
const {
    LINE_STROKE_COLOR,
    REF_DOT_STROKE_COLOR,
    REF_DOT_FILL_COLOR,
    OPACITY_HIDE,
    OPACITY_SHOW,
    OBJECTIVE_LABEL,
    TODAY_LABEL,
    AXIS_LINE_COLOR,
} = require('../../raConstants/RAObjectiveTracking');
class RAProjectionChart extends React.Component {
    constructor(props) {
        super(props);
        this.moneyFormatter = this.moneyFormatter.bind(this);
        this.handleObjectiveClick = this.handleObjectiveClick.bind(this);
        this.handleResetCutomToolTipHandle = this.handleResetCutomToolTipHandle.bind(
            this,
        );
        this.updateTooltip = this.updateTooltip.bind(this);
        this.getReferenceDot = this.getReferenceDot.bind(this);
        this.getLineShape = this.getLineShape.bind(this);
        this.getActiveDot = this.getActiveDot.bind(this);
        this.handleActiveDotClick = this.handleActiveDotClick.bind(this);
        this.state = {
            strokeWidth: 0,
            disableChartTooltip: false,
            buttonToggle: '',
        };
        this.area = null;
        this.tooltip = null;
        this.chartP = null;
        this.point = {};
    }

    moneyFormatter(tick) {
        return tick
            ? `${CurrencyHelper.formatAmount(tick, null, 0)} ${EURO_SYMBOL}`
            : '';
    }

    handleObjectiveClick() {
        this.setState({
            strokeWidth: 1,
            disableChartTooltip: true,
            buttonToggle: OBJECTIVE_LABEL,
        });
        this.tooltip.style.opacity = OPACITY_SHOW;
        this.updateTooltip();
    }

    handleResetCutomToolTipHandle(chart) {
        const { data, disabled } = this.props;
        const currentData = disabled || isEmpty(data) ? DATA : data;
        const noOfChartPoints = currentData.length;
        if (
            this.state.disableChartTooltip &&
            get(chart, 'activeLabel', 0) !== noOfChartPoints - 1
        ) {
            this.setState({
                disableChartTooltip: false,
            });
            this.tooltip.style.opacity = OPACITY_HIDE;
        }
    }

    updateTooltip() {
        const centerXValue = this.area.getAttribute('cx');
        const centerYValue = this.area.getAttribute('cy');
        const tooltipPositionX = Math.round(centerXValue) - 150;
        const tooltipPositionY = Math.round(centerYValue) - 50;
        this.tooltip.style.transform = `translate(${tooltipPositionX}px, ${tooltipPositionY}px)`;
    }

    getReferenceDot({ cx, cy }) {
        const { isObjectiveTracking, smallObjectiveDot, disabled } = this.props;
        let shape;
        let strokeWidth = '5';
        let radius = '15';
        if (smallObjectiveDot) {
            strokeWidth = '3';
            radius = '10';
        }
        if (isObjectiveTracking && !disabled) {
            shape = (
                <g>
                    <defs>
                        <filter
                            id="dropshadow"
                            x="-40%"
                            y="-40%"
                            width="180%"
                            height="180%"
                            filterUnits="userSpaceOnUse"
                        >
                            <feGaussianBlur
                                in="SourceAlpha"
                                stdDeviation="1"
                                result="blur"
                            />
                            <feOffset dx="0" dy="1" in="blur" result="offset" />
                            <feComponentTransfer>
                                <feFuncA
                                    type="linear"
                                    slope="0.5"
                                    in="offset"
                                    result="transfer"
                                />
                            </feComponentTransfer>
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    <circle
                        ref={ref => (this.area = ref)}
                        onClick={this.handleObjectiveClick}
                        cx={cx}
                        cy={cy}
                        strokeWidth={strokeWidth}
                        r={radius}
                        stroke={REF_DOT_STROKE_COLOR}
                        fill={REF_DOT_FILL_COLOR}
                        cursor="pointer"
                        style={{ filter: 'url(#dropshadow)' }}
                    ></circle>
                </g>
            );
        }
        return shape;
    }

    getLineShape({ x1, y1, x2, y2, x }) {
        let shape;
        if (this.props.isObjectiveTracking && !this.props.disabled) {
            shape = (
                <g>
                    <defs>
                        <marker
                            id="arrow"
                            markerWidth="20"
                            markerHeight="20"
                            refX="0"
                            refY="3"
                            orient="-90deg"
                            markerUnits="strokeWidth"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M0,0 L0,6 L9,3 z"
                                fill={LINE_STROKE_COLOR}
                            ></path>
                        </marker>
                    </defs>
                    <line
                        x={x}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={LINE_STROKE_COLOR}
                        strokeWidth={this.state.strokeWidth}
                        markerStart="url(#arrow)"
                        strokeDasharray="2"
                        fill="none"
                        fillOpacity="1"
                    />
                </g>
            );
        }
        return shape;
    }

    handleActiveDotClick() {
        if (this.chartP.state.activeLabel === 0) {
            this.setState({
                buttonToggle: TODAY_LABEL,
                strokeWidth: 1,
            });
        }
    }

    getActiveDot({ cx, cy }) {
        return (
            <g>
                <circle
                    onClick={this.handleActiveDotClick}
                    cx={cx}
                    cy={cy}
                    strokeWidth="2"
                    r="8"
                    opacity="1"
                    stroke={''}
                    fill="white"
                    cursor="pointer"
                />
            </g>
        );
    }

    render() {
        const {
            data,
            loading,
            disabled,
            small,
            syncId,
            margin,
            yDomain,
            yTickMargin,
            yTickFormatter,
            yWidth,
            years,
            isObjectiveTracking,
            tickInfo,
            objAmount,
            actualAmount,
        } = this.props;
        const currentClassname = classnames(
            'ra-projection-chart',
            {
                'ra-projection-chart--loading': loading,
                'ra-projection-chart--disabled': disabled,
            },
            small
                ? 'ra-projection-chart--small'
                : 'ra-projection-chart--medium',
        );

        const tooltip =
            disabled || this.state.disableChartTooltip ? (
                <span />
            ) : (
                <Tooltip
                    active={this.state.disableChartTooltip}
                    content={<RAProjectionChartTooltip />}
                    cursor={<RACustomizedCursorLine />}
                />
            );
        const currentData = disabled || isEmpty(data) ? DATA : data;
        let customTickElement;
        const noOfChartPoints = currentData.length;
        if (isObjectiveTracking && !disabled) {
            const showPointsList = getShowPonitsAndDayTerm(noOfChartPoints);
            customTickElement = (
                <RACustomTickWithObjective
                    handleObjectiveClick={this.handleObjectiveClick} // eslint-disable-line
                    disabled={disabled}
                    tickInfo={tickInfo}
                    showPointsList={showPointsList}
                    noOfChartPoints={noOfChartPoints}
                    activeButtonLabel={this.state.buttonToggle}
                />
            );
        } else {
            customTickElement = (
                <RAProjectionChartCustomTick
                    handleObjectiveClick={this.handleObjectiveClick} // eslint-disable-line
                    disabled={disabled}
                    years={years}
                />
            );
        }

        const remainingAmount = objAmount - actualAmount;
        const getYMinDataDomain = yMinData => {
            if (yMinData > objAmount && objAmount !== 0) {
                return objAmount;
            }
            return yMinData;
        };

        const getYMaxDataDomain = yMaxData => {
            if (yMaxData > objAmount) {
                return yMaxData;
            }
            return objAmount;
        };
        const yAxisDomain = yDomain || [getYMinDataDomain, getYMaxDataDomain];
        let referenceLineXValue;
        if (this.state.buttonToggle === OBJECTIVE_LABEL) {
            referenceLineXValue = noOfChartPoints - 1;
        } else {
            referenceLineXValue = 0;
        }
        const graph = (
            <div className={currentClassname}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={currentData}
                        margin={margin || { top: 40, right: 10 }}
                        syncId={syncId}
                        onMouseMove={this.handleResetCutomToolTipHandle}
                        ref={ref => (this.chartP = ref)}
                    >
                        {tooltip}
                        <CartesianGrid vertical={false} strokeDasharray="2" />
                        <YAxis
                            width={yWidth || 100}
                            tickLine={false}
                            tickFormatter={
                                yTickFormatter || this.moneyFormatter
                            }
                            tickMargin={yTickMargin || 10}
                            axisLine={false}
                            domain={yAxisDomain}
                            scale="linear"
                        />
                        <XAxis
                            domain={[0, 'dataMax']}
                            tickLine={false}
                            tick={customTickElement}
                            tickMargin={10}
                            padding={{ left: 20, right: 25 }}
                            scale="point"
                            interval={0}
                            axisLine={{
                                stroke: AXIS_LINE_COLOR,
                                strokeWidth: 1,
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey="lowRange"
                            className="ra-projection-chart__area--grey"
                            fill={''}
                            stroke={''}
                            dot={false}
                            activeDot={false}
                        />
                        <Area
                            type="monotone"
                            dataKey="highRange"
                            className="ra-projection-chart__area--light-blue"
                            fill={''}
                            stroke={''}
                            dot={false}
                            activeDot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="low"
                            className="ra-projection-chart__line--grey"
                            stroke={''}
                            dot={{ opacity: 0 }}
                            activeDot={this.getActiveDot}
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="up"
                            className="ra-projection-chart__line--light-blue"
                            stroke={''}
                            dot={{ opacity: 0 }}
                            activeDot={this.getActiveDot}
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="mid"
                            className="ra-projection-chart__line--default"
                            stroke={''}
                            dot={{ r: 6 }}
                            activeDot={this.getActiveDot}
                            isAnimationActive={false}
                        />
                        <ReferenceLine
                            x={referenceLineXValue}
                            shape={this.getLineShape}
                        />
                        <ReferenceDot
                            x={noOfChartPoints - 1}
                            y={objAmount}
                            shape={this.getReferenceDot}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
                <div
                    className="ra-projection-chart__custom-tooltip"
                    ref={ref => (this.tooltip = ref)}
                >
                    <div className="ra-projection-chart__custom-tooltip-content">
                        <FormattedText
                            className="ra-projection-chart__custom-tooltip-title"
                            value="raObjectiveTracking-objectiveText"
                        />
                        <span className="ra-projection-chart__custom-tooltip-balance">
                            <RAOneLineSymbolAmount
                                amountObject={{
                                    amount: objAmount,
                                    currency: EURO_TEXT,
                                }}
                            />
                        </span>
                        <FormattedText
                            className="ra-projection-chart__custom-tooltip-title"
                            value="raObjectiveTracking-subtractText"
                        />
                        <span className="ra-projection-chart__custom-tooltip-term">
                            <RAOneLineSymbolAmount
                                amountObject={{
                                    amount: remainingAmount,
                                    currency: EURO_TEXT,
                                }}
                            />
                        </span>
                    </div>
                </div>
            </div>
        );

        return graph;
    }
}

RAProjectionChart.propTypes = {
    actualAmount: PropTypes.number,
    data: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    height: PropTypes.number,
    isObjectiveTracking: PropTypes.bool,
    loading: PropTypes.bool,
    margin: PropTypes.object,
    objAmount: PropTypes.number,
    small: PropTypes.bool,
    smallObjectiveDot: PropTypes.bool,
    syncId: PropTypes.string,
    tickInfo: PropTypes.object,
    yDomain: PropTypes.array,
    yTickFormatter: PropTypes.func,
    yTickMargin: PropTypes.number,
    yWidth: PropTypes.number,
    years: PropTypes.number.isRequired,
};

RAProjectionChart.defaultProps = {
    isObjectiveTracking: false,
    objAmount: 0,
    smallObjectiveDot: false,
};

module.exports = RAProjectionChart;
